<template>
  <div>
    <b-alert variant="secondary" show>
      <b-row cols="12" class="mb-1 alert-heading">
        <b-col class="d-flex align-items-center justify-content-between">
          <b-form-checkbox
            @change="hideShowFilter"
            :showFilters="true"
            checked="true"
            ><b>show filters</b></b-form-checkbox
          >
          <b-form-checkbox
            name="check-button"
            inline
            v-model="filters.deleted"
            @input="onFiltersChange"
          >
            <b>show deleted entries</b>
          </b-form-checkbox>
          <b-input-group style="width: 400px" class="input-group-merge">
            <b-input-group-prepend is-text class="left-br-10">
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              placeholder="Search"
              class="right-br-10"
              v-model="filters.search"
              @input="onSearch"
            />
          </b-input-group>
          <b-button-group size="sm">
            <b-button
              size="sm"
              v-if="$can('create', 'contact')"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="gradient-primary"
              @click="$router.push('/contacts/form/new')"
              >New Contact</b-button
            >
            <b-dropdown size="sm" right text="Actions">
              <b-dropdown-form>
                <b-button
                  block
                  size="sm"
                  class="w-100"
                  variant="gradient-success"
                  @click="downloadCSV"
                >
                  <span class="text-nowrap"> Export Customer.csv </span>
                </b-button>
                <b-dropdown-divider></b-dropdown-divider>

                <b-button
                  block
                  size="sm"
                  class="w-100"
                  variant="gradient-success"
                  @click="onSample"
                >
                  <span class="text-nowrap">
                    Customer Data Upload Sample.xlxs
                  </span>
                </b-button>
              </b-dropdown-form>
            </b-dropdown>
          </b-button-group>
        </b-col>
      </b-row>
    </b-alert>

    <b-row v-if="filtersHidden">
      <b-col sm="12" md="2" lg="2">
        <master-select
          class="mb-1 borderRadius-10"
          v-model="filters.country"
          :reduce="(c) => c.lbl_id"
          :options="$store.state.master.country"
          :loading="this.$store.state.master.loading.country"
          aria-placeholder="Country"
          placeholder="Country"
          label="lbl_name"
          @input="onFiltersChange"
        />
      </b-col>
      <b-col  sm="12" md="3" lg="3">
        <v-select
          class="mb-1 borderRadius-10"
          v-model="filters.sort_by"
          :reduce="(c) => c.value"
          :options="sortByOptions"
          placeholder="Sort By"
          label="title"
          @input="onFiltersChange"
          :clearable="false"
        />
      </b-col>
      <b-col sm="6" md="2" lg="2">
        <b-button
          v-ripple.400="'rgba(186, 191, 199, 0.15)'"
          variant="outline-secondary"
          class="w-100 mb-1 borderRadius-10"
          @click="clearFilters"
          >Reset</b-button
        >
      </b-col>
    </b-row>

    <b-card no-body class="mb-0" v-if="$can('read', 'contact')">
      <b-table
        small
        hover
        bordered
        :items="contact_list"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="No records found"
        @row-clicked="onRowClicked"
      >
        <template #head(sr)="data">
          <b-badge v-if="pagination.totalRows" variant="success">{{
            pagination.totalRows
          }}</b-badge>
        </template>
        <template #cell(additionalInfoCustomer)="data"> 
          <div style="display: flex;justify-content: center;align-items: center;">
                  <feather-icon
                    style="cursor: pointer"
                    icon="InfoIcon"
                    size="23"
                    @mouseenter="handleHoverCustomerId(data.item.vendor_id)"
                    @mouseleave="handleReleasedCustomerId()"
                  />
                </div>
         <div v-if="hoverId === data.item.vendor_id">     
          <hover-metadata-component
                  :item="{
                    ...data.item,
                    createdAt: formatDate(data.item.createdAt),
                    updatedAt: formatDate(data.item.updatedAt),
                  }"
                  :idField="'vendor_id'"
                  :createdAt="data.item.createdAt"
                  :createdBy="data.item.createdBy_name"
                  :updatedAt="data.item.updatedAt"
                  :updatedBy="data.item.updatedBy_name"
                  :isHovered="hoverId === data.item.vendor_id"
                  :showAbove="data.index >= itemsInRow - 3? true : false "
                />
              </div>          
        </template>
        <template #cell(sr)="data">
          {{
            data.index +
            1 +
            (pagination.currentPage - 1) * pagination.rowsPerPage
          }}
        </template>

        <!-- Column: User -->
        <template #cell(user)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.avatar"
                :text="avatarText(data.item.fullName)"
                :variant="`light-${resolveUserRoleVariant(data.item.role)}`"
                :to="{
                  name: 'apps-users-view',
                  params: { id: data.item.id },
                }"
              />
            </template>
            <b-link
              :to="{ name: 'apps-users-view', params: { id: data.item.id } }"
              class="font-weight-bold d-block text-nowrap"
            >
              {{ data.item.fullName }}
            </b-link>
            <small class="text-muted">@{{ data.item.username }}</small>
          </b-media>
        </template>

        <!-- Column: Role -->
        <template #cell(role)="data">
          <div class="text-nowrap">
            <feather-icon
              :icon="resolveUserRoleIcon(data.item.role)"
              size="18"
              class="mr-50"
              :class="`text-${resolveUserRoleVariant(data.item.role)}`"
            />
            <span class="align-text-top text-capitalize">{{
              data.item.role
            }}</span>
          </div>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${resolveUserStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ data.item.status }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <feather-icon
            v-if="$can('update', 'contact')"
            icon="EditIcon"
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="outline-primary"
            title="Edit"
            class="mr-1"
            @click="
              () => {
                $router.push(`/contacts/form/${data.item.vendor_id}`);
              }
            "
          />
          <!-- <feather-icon
            v-else-if="$can('read', 'contact')"
            icon="EyeIcon"
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            v-b-tooltip.hover.v-primary
            variant="outline-primary"
            title="Edit"
            class="mr-1"
            @click="
              () => {
                $router.push(`/contacts/form/${data.item.vendor_id}`);
              }
            "
          /> -->
          <feather-icon
            v-if="$can('update', 'contact') || $can('create', 'contact')"
            :id="`contract-row-${data.item.vendor_id}-copy-icon`"
            icon="CopyIcon"
            size="16"
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            v-b-tooltip.hover.v-primary
            variant="outline-primary"
            title="Copy"
            class="mr-1 cursor-pointer"
            @click.stop="
              $router.push(`/contacts/form/${data.item.vendor_id}?copy=1`)
            "
          />
          <feather-icon
            v-if="$can('delete', 'contact') && !data.item.deleted"
            :id="`contract-row-${data.item.contract_id}-delete-icon`"
            icon="TrashIcon"
            size="16"
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            v-b-tooltip.hover.v-primary
            variant="outline-primary"
            title="Delete Customer"
            class="mr-1 cursor-pointer"
            @click.stop="
              () => {
                showModal(
                  'Are you sure want to delete this customer?',
                  'delete'
                ).then((value) => {
                  if (value) deleteContact(data.item.vendor_id, 1);
                });
              }
            "
          />

          <!-- @click.stop="deleteContact(data.item.vendor_id, 1)" -->
          <feather-icon
            v-if="$can('read', 'contact')"
            icon="EyeIcon"
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            v-b-tooltip.hover.v-primary
            variant="outline-primary"
            title="View"
            class="mr-1"
            @click.stop="
              () => {
                $router.push(`/contacts/overview/${data.item.vendor_id}`);
              }
            "
          />
        </template>
      </b-table>
      <template #footer>
        <div>
          <b-row>
            <b-col
              cols="12"
              class="d-flex align-items-end justify-content-end justify-content-sm-center"
            >
              <b-pagination
                v-model="pagination.currentPage"
                :total-rows="pagination.totalRows"
                :per-page="pagination.rowsPerPage"
                @change="onChange"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
                :limit="15"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </template>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BCardHeader,
  BCardBody,
  VBTooltip,
  BFormCheckbox,
  BButtonGroup,
  BInputGroup,
  BInputGroupPrepend,
  BAlert,
  BDropdownForm,
  BDropdownDivider,
} from "bootstrap-vue";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import * as VendorServices from "@/apiServices/VendorServices";
import { labels } from "@/utils/constants";
import ToastificationContentVue from "@core/components/toastification/ToastificationContent.vue";
import { downloadFromURL } from "@/utils/helpers";
import { TokenService } from "@/apiServices/storageService";
import UploadSheets from "@/views/components/UploadSheets.vue";
import AppCollapse from "@core/components/app-collapse/AppCollapse.vue";
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";
import showModal from "../../components/ModalConfirmation";
import * as XLSX from "xlsx";
import moment from "moment";
import HoverMetadataComponent from "@/views/components/HoverMetadataComponent.vue";
import FeatherIcon from "@/@core/components/feather-icon/FeatherIcon.vue";
export default {
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BCardHeader,
    BCardBody,
    vSelect,
    VBTooltip,
    ToastificationContentVue,
    BFormCheckbox,
    BButtonGroup,
    UploadSheets,
    BInputGroup,
    BInputGroupPrepend,
    AppCollapseItem,
    AppCollapse,
    BAlert,
    BDropdownForm,
    BDropdownDivider,
    HoverMetadataComponent,
    FeatherIcon
  },

  data() {
    return {
      hoverId:null,
      moment:moment,
      showModalUpload: false,
      hoveredCustomerId: null,
      pagination: {
        currentPage: 1,
        totalRows: 1,
        rowsPerPage: 20,
        perPageOptions: [20, 50, 100],
      },
      tableColumns: [
        { label: "SR", key: "sr", thStyle: { width: "6rem" } },
        { label: "Company Name", key: "company" },
        { label: "Country", key: "country_name", thStyle: { width: "20rem" } },
        { key: "actions" },
        {
          label: "LOGS",
          key: "additionalInfoCustomer",
        },
      ],
      contact_list: [],
      filtersHidden: true,
      filters: {
        search: "",
        country: "",
        deleted: false,
        sort_by:"company^asc"
      },
      sortByOptions: [
        { title: "Sort Company Name (A - Z)", value: "company^asc" },
        { title: "Sort Company Name (Z - A)", value: "company^desc" },
      ],
      itemsInRow: 20,
    };
  },

  methods: {
    formatDate(dateString) {
      console.log(dateString);
      if (!dateString) return "";
      if (
        dateString.startsWith("0000-00-00") ||
        !moment(dateString).isValid()
      ) {
        return "";
      } else {
        let val = moment(dateString).format("DD-MM-YYYY");
        console.log(val, "after-conversion");
        return val;
      }
    },
    handleFormatDate(dateValue) {
      if (!dateValue) return "";
      else {
        let date = new Date(dateValue);
        return date.toLocaleDateString("en-GB").replace(/\//g, "-");
      }
    },
    handleHoverCustomerId(customerId) {
      console.log("customerId", customerId);
      this.hoverId = customerId;
    },
    handleReleasedCustomerId() {
      this.hoverId = null;
    },
    onSample() {
      let sheetName = "";
      let excelName = "";
      let data = [
        {
          COMPANY_NAME: "",
          ADDRESS: "",
          CITY: "",
          ZIP_CODE: "",
        },
      ];

      sheetName = "Customers";
      excelName = "Corsymo_Customer_Import_Sample.xlsx";

      var dataFinal = XLSX.utils.json_to_sheet(data);
      var wb = XLSX.utils.book_new();
      wb["!cols"] = [{ wch: 25 }];
      XLSX.utils.book_append_sheet(wb, dataFinal, sheetName); // sheetAName is name of Worksheet
      XLSX.writeFile(wb, excelName);
    },
    hideShowFilter() {
      this.filtersHidden = !this.filtersHidden;
    },
    onRowClicked(item, index, event) {
      if (this.$can("update", "contact")) {
        this.$router.push(`/contacts/form/${item.vendor_id}`);
      }
    },
    downloadCSV() {
      try {
        const token = TokenService.getToken();

        if (!token) {
          alert("Error in getting token");
        }

        let urlStr = `${process.env.VUE_APP_BASEURL}/vendor/sheet/download?authorization=${token}`;

        downloadFromURL(urlStr, "Customers", "csv");
      } catch (error) {
        console.error(`Error in downloadCSV `, error);
      }
    },

    clearFilters() {
      this.filters = {
        search: "",
        country: "",
        deleted: false,
        sort_by:"company^asc"
      };
      this.pagination = {
        currentPage: 1,
        totalRows: 1,
        rowsPerPage: 20,
        perPageOptions: [20, 50, 100],
      };
      this.onChange();
    },

    onFiltersChange() {
      this.pagination.currentPage = 1;
      this.onChange();
    },

    showModal,

    onChange() {
      this.$nextTick(() => {
        this.getVendors();
        localStorage.setItem(
          "contact-SearchAndPagination",
          JSON.stringify({
            filters: this.filters,
            pagination: this.pagination,
          })
        );
      });
    },

    onSearch() {
      if (this.debouce) clearTimeout(this.debouce);
      this.debouce = setTimeout(() => {
        this.onFiltersChange();
      }, 1000);
    },

    async getVendors() {
      try {
        const response = await VendorServices.getAllVendor({
          search: this.filters.search,
          country: this.filters.country,
          deleted: this.filters.deleted,
          limit: this.pagination.rowsPerPage,
          page: this.pagination.currentPage,
          sort_by: this.filters.sort_by
        });
        if (!response.data.status) {
          return this.$toast({
            component: ToastificationContentVue,
            props: {
              title: response.data.message || "Issue in fetching Data",
              icon: "EditIcon",
              variant: "failure",
            },
          });
        }
        this.contact_list = response.data.data.data;
        this.itemsInRow = this.contact_list.length;
        if (this.pagination.currentPage == 1) {
          this.pagination.totalRows = response.data.data.pagination.total;
        }
      } catch (err) {
        console.error("Error => ", err.message);
      }
    },

    async deleteContact(vendor_id) {
      try {
        const response = await VendorServices.deleteVendor({ vendor_id });
        if (response.data.status) {
          this.$toast({
            component: ToastificationContentVue,
            props: {
              title: response.data.message || "Success",
              icon: "EditIcon",
              variant: "success",
            },
          });
          this.onChange();
        } else {
          this.$toast({
            component: ToastificationContentVue,
            props: {
              title: response.data.message || "Failed",
              icon: "EditIcon",
              variant: "failure",
            },
          });
        }
      } catch (error) {
        console.error("Error in delete contact ", error);
      }
    },
  },

  beforeMount() {
    this.$store.dispatch("master/setLabelMaster", { key: labels.country });

    const contactlocalStorageStr = localStorage.getItem(
      "contact-SearchAndPagination"
    );
    if (contactlocalStorageStr) {
      const obj = JSON.parse(contactlocalStorageStr);
      this.filters = { ...this.filters, ...obj.filters };
      this.filters.sort_by = "company^asc";
      this.pagination = { ...this.pagination, ...obj.pagination };
    }
    this.getVendors();
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.per-page-selector {
  width: 90px;
}

.borderRadius-10 {
  & .vs__dropdown-toggle {
    border-radius: 10px !important;
  }
  border-radius: 10px !important;
}

.right-br-10 {
  & button {
    border-radius: 0 10px 10px 0 !important;
  }
  border-radius: 0 10px 10px 0 !important;
}

.left-br-10 {
  & .input-group-text {
    border-radius: 10px 0 0 10px !important;
  }
  border-radius: 10px 0 0 10px !important;
}
</style>
